// Bulk Shade Modal - Complete with Auto-Select & Validation & Dynamic Rebuilding
// Optimized version with race condition fixes and close confirmation

(function() {
  'use strict';

  // CRITICAL: Prevent double initialization
  if (window._bulkShadeModalInitialized) {
    console.warn('⚠️ Bulk shade modal already initialized, skipping duplicate');
    return;
  }
  window._bulkShadeModalInitialized = true;

  let modalState = {
    isOpen: false,
    isLoading: false,
    needsRebuild: false,
    currentProductUrl: null,
    isRebuilding: false
  };

  // Track pending operations to prevent race conditions
  let pendingAutoSelect = null;

  // Cart fetch caching to prevent race conditions
  let cartFetchPromise = null;
  let cartFetchTimestamp = 0;
  const CART_CACHE_DURATION = 2000; // 2 seconds

  // Auto-selection flag to prevent blocking during product switch
  window._isAutoSelecting = false;

  // ============================================================================
  // CART FETCH WITH CACHING
  // ============================================================================
  function getCart(forceRefresh = false) {
    const now = Date.now();
    
    // Return cached promise if recent
    if (!forceRefresh && cartFetchPromise && (now - cartFetchTimestamp) < CART_CACHE_DURATION) {
      console.log('Using cached cart data');
      return cartFetchPromise;
    }
    
    console.log('Fetching fresh cart data');
    cartFetchTimestamp = now;
    cartFetchPromise = fetch('/cart.js')
      .then(response => response.json())
      .catch(error => {
        console.error('Cart fetch failed:', error);
        // Clear cache on error
        cartFetchPromise = null;
        throw error;
      });
    
    return cartFetchPromise;
  }

  // ============================================================================
  // MONEY FORMATTING HELPER
  // ============================================================================
  function formatMoney(cents) {
    // Convert cents to amount
    const amount = (cents / 100).toFixed(2);
    
    // Try to get money format from various sources
    let moneyFormat = '£{{amount}}'; // Default to GBP
    
    // Check theme settings
    if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
      moneyFormat = window.theme.moneyFormat;
    }
    // Check Shopify settings
    else if (typeof Shopify !== 'undefined' && Shopify.money_format) {
      moneyFormat = Shopify.money_format;
    }
    // Check theme Currency object
    else if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, moneyFormat);
    }
    
    // Format the money
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  // ============================================================================
  // SOLD-OUT SHADE SWAP (Life Color Plus → 3Deluxe)
  // ============================================================================
  // A sold-out shade whose exact number exists in stock on the partner line gets
  // the substitute offered beneath it. The pairs are emitted by the snippet so
  // the rebuild path below can rebuild swaps too — without that, the feature
  // disappears the first time the shopper switches product on a combined listing.

  var _swapConfig;

  function getSwapConfig() {
    if (_swapConfig !== undefined) return _swapConfig;
    _swapConfig = null;
    var el = document.getElementById('shade-swap-data');
    if (el) {
      try {
        _swapConfig = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('Shade swap data is not valid JSON, swaps disabled', e);
      }
    }

    // Liquid caps some array loops. If it capped this one, the shades whose twin
    // sorts late are silently missing rather than erroring — so say so loudly.
    if (_swapConfig &&
        typeof _swapConfig.targetVariantsSeen === 'number' &&
        typeof _swapConfig.targetVariantsSize === 'number' &&
        _swapConfig.targetVariantsSeen < _swapConfig.targetVariantsSize) {
      console.warn(
        'Shade swap: Liquid saw only ' + _swapConfig.targetVariantsSeen + ' of ' +
        _swapConfig.targetVariantsSize + ' substitute variants — later shades will be ' +
        'missing their swap. Raise the loop limit in bulk-shade-modal.liquid.'
      );
    }

    return _swapConfig;
  }

  // ?swap=open / ?swap=button previews the other presentation without touching
  // the theme setting.
  function getSwapStyle() {
    var m = /[?&]swap=(open|button)/.exec(window.location.search);
    if (m) return m[1];
    var cfg = getSwapConfig();
    return (cfg && cfg.style === 'button') ? 'button' : 'open';
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // A tick, not exchange arrows — mirrors snippets/bulk-shade-modal-swap-icon.liquid.
  // The caption says the shade is the same and in stock; a swap glyph would read
  // as giving something up.
  var SWAP_ICON = '<svg class="shade-swap-icon" width="11" height="9" viewBox="0 0 11 9" ' +
    'fill="none" aria-hidden="true" focusable="false"><path d="M1 4.6 4 7.6 10 1.4" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Mirrors snippets/bulk-shade-modal-swap-facts.liquid. Keep the two in step —
  // this path builds the modal when the shopper switches product on a combined
  // listing, so copy that only lives in the Liquid disappears after a switch.
  // The flag is an inline SVG because Windows has no regional-indicator glyphs:
  // the 🇮🇹 emoji renders as the letters "IT" in Chrome on Windows.
  var FACT_FLAG = '<svg class="shade-swap-flag" width="12" height="9" viewBox="0 0 12 9" ' +
    'aria-hidden="true" focusable="false"><rect width="4" height="9" fill="#008C45"/>' +
    '<rect x="4" width="4" height="9" fill="#F4F5F0"/><rect x="8" width="4" height="9" fill="#CD212A"/></svg>';

  // Each benefit is its own nowrap span so a wrap breaks BETWEEN benefits,
  // never mid-phrase ("Same trusted / manufacturer"). The mix ratio carries the
  // --ratio modifier because CSS hides it below 600px.
  //
  // Two variants: cross-brand pairs (Life Color Plus -> 3Deluxe) reassure with
  // "Same trusted manufacturer"; same-brand pairs (new Majirel -> old Majirel)
  // say "Previous 50ml packaging" instead -- that line exists to reassure about
  // a BRAND switch, which isn't what's happening for a same-range repack. See
  // snippets/bulk-shade-modal-swap-facts.liquid, which this mirrors.
  function factsFallback(sameBrand) {
    var secondFact = sameBrand
      ? '<span class="shade-swap-fact">Previous 50ml packaging</span>'
      : '<span class="shade-swap-fact">Same trusted manufacturer ' + FACT_FLAG + '</span>';
    return '<p class="shade-swap-why" data-same-brand="' + (sameBrand ? 'true' : 'false') + '">' +
      '<span class="shade-swap-fact">Exact shade match</span>' +
      secondFact +
      '<span class="shade-swap-fact shade-swap-fact--ratio">1:1.5 mix ratio</span>' +
      '</p>';
  }

  // Prefer the copy Liquid already rendered (snippets/bulk-shade-modal-swap-facts
  // .liquid) so the wording lives in ONE place -- but ONLY when it was rendered
  // for the SAME kind of pair as the current config: a stale cross-brand line
  // left over from before a combined-listing switch must not be reused under a
  // same-brand pair (or vice versa). The PDP grid module reads the same node.
  function factsHtml() {
    var cfg = getSwapConfig();
    var sameBrand = !!(cfg && cfg.sameBrand);
    var rendered = document.querySelector('.shade-swap-why');
    if (rendered && (rendered.getAttribute('data-same-brand') === 'true') === sameBrand) {
      return rendered.outerHTML;
    }
    return factsFallback(sameBrand);
  }

  var STOCK_HTML = '<span class="shade-variant-stock">' +
    '<span class="shade-variant-stock-status">&nbsp;</span></span>';

  function swapHeaderHTML(style, shade, panelId) {
    var label = 'Same shade in stock now';
    if (style === 'button') {
      return '<button type="button" class="shade-swap-toggle" aria-expanded="false" ' +
        'aria-controls="' + escapeHTML(panelId) + '">' + SWAP_ICON +
        '<span>' + label + '</span><span class="shade-swap-chevron" aria-hidden="true"></span></button>';
    }
    return '<p class="shade-swap-caption">' + SWAP_ICON + '<span>' + label + '</span></p>';
  }

  function swapBrandShort() {
    var cfg = getSwapConfig();
    return (cfg && cfg.brandShort) || '3Deluxe';
  }

  function swapBrand() {
    var cfg = getSwapConfig();
    return (cfg && cfg.brand) || '3Deluxe Professional';
  }

  function swapTargetSize() {
    var cfg = getSwapConfig();
    return (cfg && cfg.targetSize) || '100ml';
  }

  // Shade thumbnails render at 40px (35px at <=600px). Rows rebuilt here used the full-size
  // featured_image.src, eagerly; match the Liquid rows instead: 100w/200w, native lazy.
  // loading/sizes come before srcset in the markup (WebKit bug 276586).
  var THUMB_SIZES = '(max-width: 600px) 35px, 40px';
  function thumbSrcset(src) {
    var base = String(src || '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'width=100 100w, ' + base + sep + 'width=200 200w';
  }

  // Builds the whole swap block for one sold-out variant. `parent` is the
  // sold-out variant (used for the held price), `pair` the in-stock substitute.
  function swapBlockHTML(parent, pair, style) {
    var cfg = getSwapConfig();
    var hold = !!(cfg && cfg.holdPrice);
    var panelId = 'shade-swap-panel-' + parent.id;
    var shade = pair.shade;

    var image = pair.image
      ? '<img class="shade-variant-image" loading="lazy" decoding="async" width="40" height="40" src="' + escapeHTML(pair.image) + '" alt="' +
        escapeHTML(swapBrand() + ' ' + shade) + '">'
      : '';

    var pricing;
    if (hold) {
      pricing =
        '<span class="shade-variant-price shade-swap-now" data-original-price="' + parent.price +
        '" data-variant-sku="' + escapeHTML(parent.sku || '') + '">' + formatMoney(parent.price) + '</span>' +
        '<span class="shade-variant-price shade-swap-was" data-original-price="' + pair.price +
        '" data-variant-sku="' + escapeHTML(pair.sku || '') + '">' + formatMoney(pair.price) + '</span>' +
        STOCK_HTML;
    } else {
      pricing =
        '<span class="shade-variant-price shade-swap-now" data-original-price="' + pair.price +
        '" data-variant-sku="' + escapeHTML(pair.sku || '') + '">' + formatMoney(pair.price) +
        '</span>' + STOCK_HTML;
    }

    return '<div class="shade-swap" data-swap-shade="' + escapeHTML(shade) + '">' +
      swapHeaderHTML(style, shade, panelId) +
      '<div class="shade-swap-panel" id="' + panelId + '"' + (style === 'button' ? ' hidden' : '') + '>' +
        '<div class="shade-variant-item shade-swap-item" data-variant-id="' + pair.id +
        '" data-inventory-quantity="' + pair.qty +
        '" data-product-handle="' + escapeHTML((cfg && cfg.targetHandle) || '') +
        '" data-swap-for="' + parent.id +
        '" data-swap-for-sku="' + escapeHTML(parent.sku || '') + '">' +
          image +
          '<div class="shade-variant-info">' +
            // No shade number: the sold-out row above already shows it.
            '<div class="shade-swap-head">' +
              '<span class="shade-swap-brand">' + escapeHTML(swapBrand()) + ' ' + escapeHTML(swapTargetSize()) + '</span>' +
            '</div>' +
            '<div class="shade-swap-pricing">' + pricing + '</div>' +
          '</div>' +
          '<div class="shade-quantity-selector">' +
            '<button type="button" class="shade-qty-btn minus" aria-label="Decrease quantity">-</button>' +
            '<input type="number" class="shade-qty-input" value="0" min="0" aria-label="Quantity">' +
            '<button type="button" class="shade-qty-btn plus" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        // Outside the row, spanning the panel — inside the info column it wrapped
        // to a second line and cost 16px on every substitute. See the Liquid.
        factsHtml() +
      '</div>' +
    '</div>';
  }

  // Re-skins swap blocks already in the DOM between the two presentations.
  function applySwapStyle(style) {
    var modal = document.getElementById('shade-modal');
    if (!modal) return;

    modal.querySelectorAll('.shade-swap').forEach(function (swap) {
      var panel = swap.querySelector('.shade-swap-panel');
      var head = swap.querySelector('.shade-swap-toggle, .shade-swap-caption');
      if (!panel || !head) return;

      var shade = swap.getAttribute('data-swap-shade') || '';
      head.outerHTML = swapHeaderHTML(style, shade, panel.id);

      if (style === 'button') {
        panel.setAttribute('hidden', '');
      } else {
        panel.removeAttribute('hidden');
      }
    });

    modal.setAttribute('data-swap-style', style);
  }

  // Opening/closing the substitute in button mode.
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('.shade-swap-toggle');
    if (!toggle) return;

    e.preventDefault();
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    var isOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));

    if (isOpen) {
      panel.setAttribute('hidden', '');
      // Closing must not smuggle a hidden selection into the cart.
      var input = panel.querySelector('.shade-qty-input');
      if (input) input.value = 0;
    } else {
      panel.removeAttribute('hidden');
    }
  });

  // ============================================================================
  // AUTO-SELECT FIRST SWATCH FUNCTIONALITY
  // ============================================================================
  function autoSelectFirstSwatch(container, force = false) {
    if (!container) return;

    const swatchFieldset = container.querySelector('.swatch');
    if (!swatchFieldset) return;

    const checkedSwatch = swatchFieldset.querySelector('.swatchInput:checked:not(:disabled)');
    if (checkedSwatch && !force) return;

    const firstAvailableSwatch = swatchFieldset.querySelector('.swatchInput:not(:disabled)');
    
    if (firstAvailableSwatch && !firstAvailableSwatch.checked) {
      firstAvailableSwatch.checked = true;
      firstAvailableSwatch.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function initAutoSelect() {
    const productContainers = document.querySelectorAll('product-info, .product-single__meta, #product');
    productContainers.forEach(container => {
      autoSelectFirstSwatch(container, true);
    });
  }

  document.addEventListener('change', function(e) {
    if (e.target.classList.contains('single-option-selector')) {
      const productContainer = e.target.closest('product-info, .product-single__meta, #product');
      if (productContainer) {
        if (pendingAutoSelect) {
          clearTimeout(pendingAutoSelect);
        }

        pendingAutoSelect = setTimeout(() => {
          autoSelectFirstSwatch(productContainer, true);
          pendingAutoSelect = null;
        }, 150);
      }
    }
  });

  // ============================================================================
  // ADD TO CART VALIDATION
  // ============================================================================
  function validateVariantSelection(formElement) {
    // PDP forms tag the variant input with .product-variant-id; PLP quick-add
    // forms (single-variant only) just use input[name="id"]. Fall back to the
    // generic selector so quick-add isn't falsely blocked.
    const variantInput = formElement.querySelector('input[name="id"].product-variant-id')
                       || formElement.querySelector('input[name="id"]');

    if (!variantInput) {
      console.warn('No variant input found');
      return false;
    }

    const variantId = variantInput.value;
    
    if (!variantId || variantId === '' || variantId === 'null') {
      return false;
    }

    if (variantInput.disabled) {
      return false;
    }

    return true;
  }

  document.addEventListener('submit', function(e) {
    // Read the id via getAttribute, NOT e.target.id. e.target here is the product
    // <form>, and HTMLFormElement's named-property getter overrides built-ins: the
    // Shopify variant input <input name="id"> shadows the element's own .id, so
    // e.target.id returns that INPUT element and .includes() throws. That threw on
    // every add-to-cart, and because it threw before the data-type check below --
    // which is the condition the real cart form actually matches -- this whole
    // guard never ran.
    if (e.target.classList.contains('product-form') ||
        (e.target.getAttribute('id') || '').includes('product-form') ||
        e.target.getAttribute('data-type') === 'add-to-cart-form') {
      
      if (!validateVariantSelection(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        alert('Please select all product options before adding to cart.');
        return false;
      }
    }
  });

  document.addEventListener('click', function(e) {
    const paymentButton = e.target.closest('.shopify-payment-button__button');
    if (paymentButton) {
      const form = paymentButton.closest('form') || 
                   document.querySelector('[data-type="add-to-cart-form"]');
      
      if (form && !validateVariantSelection(form)) {
        e.preventDefault();
        e.stopPropagation();
        alert('Please select all product options before purchasing.');
        return false;
      }
    }
  });

  document.addEventListener('click', function(e) {
    const addToCartBtn = e.target.closest('.product-form__submit, [name="add"]');
    if (addToCartBtn) {
      const form = addToCartBtn.closest('form');
      if (form && !validateVariantSelection(form)) {
        e.preventDefault();
        e.stopPropagation();
        alert('Please select all product options before adding to cart.');
        return false;
      }
    }
  });

  // ============================================================================
  // BULK SHADE MODAL FUNCTIONALITY
  // ============================================================================
  
  function getModalElements() {
    return {
      modal: document.getElementById('shade-modal'),
      overlay: document.getElementById('shade-modal-overlay'),
      closeBtn: document.querySelector('.close-shade-modal'),
      bulkAddBtn: document.getElementById('bulk-add-to-cart'),
      confirmModal: document.getElementById('shade-confirm-modal'),
      confirmOverlay: document.getElementById('shade-confirm-overlay')
    };
  }

  // ============================================================================
  // CHECK IF USER HAS MADE SELECTIONS
  // ============================================================================
  function hasSelections() {
    const modal = document.getElementById('shade-modal');
    if (!modal) return false;
    
    const allVariantItems = modal.querySelectorAll('.shade-variant-item');
    return Array.from(allVariantItems).some(item => {
      // A row replaced by a substitute has no stepper of its own.
      const input = item.querySelector('.shade-qty-input');
      if (!input) return false;
      const quantity = parseInt(input.value);
      return quantity > 0;
    });
  }

  // ============================================================================
  // CUSTOM CONFIRMATION MODAL
  // ============================================================================
  function showConfirmationModal() {
    return new Promise((resolve) => {
      const confirmModal = document.getElementById('shade-confirm-modal');
      const confirmOverlay = document.getElementById('shade-confirm-overlay');
      
      if (!confirmModal || !confirmOverlay) {
        // Fallback to browser confirm if modal doesn't exist
        resolve(confirm(
          'You have selected shades that haven\'t been added to your cart.\n\n' +
          'Click "OK" to add them to your cart now, or "Cancel" to clear selections and close.'
        ));
        return;
      }

      // Show confirmation modal
      confirmOverlay.classList.add('active');
      confirmModal.classList.add('active');

      // Handle Add to Cart button
      const addButton = confirmModal.querySelector('.confirm-add-btn');
      const cancelButton = confirmModal.querySelector('.confirm-cancel-btn');

      function cleanup() {
        confirmModal.classList.remove('active');
        confirmOverlay.classList.remove('active');
        addButton.removeEventListener('click', handleAdd);
        cancelButton.removeEventListener('click', handleCancel);
        confirmOverlay.removeEventListener('click', handleCancel);
      }

      function handleAdd() {
        cleanup();
        resolve(true);
      }

      function handleCancel() {
        cleanup();
        resolve(false);
      }

      addButton.addEventListener('click', handleAdd);
      cancelButton.addEventListener('click', handleCancel);
      confirmOverlay.addEventListener('click', handleCancel);
    });
  }

  function rebuildModalForProduct(productData) {
    console.log('Rebuilding modal for new product...', productData);
    
    const modal = document.getElementById('shade-modal');
    const modalBody = document.querySelector('.shade-modal-body .shade-variant-list');
    
    if (!modalBody) {
      console.warn('Modal body not found');
      return Promise.reject('Modal body not found');
    }

    // Mark modal as rebuilding
    if (modal) {
      modal.setAttribute('data-rebuilding', 'true');
      modalState.isRebuilding = true;
    }

    modalBody.innerHTML = '';
    // The scroller is .shade-modal-body, which outlives the rebuild: without this a list rebuilt
    // for another product (e.g. opened from the added-to-basket panel) kept the previous list's
    // scroll offset and opened near the bottom. The rebuild usually runs while the modal is
    // display:none, where setting scrollTop is ignored, so showModal() applies it once visible.
    modalState.scrollTopOnShow = true;

    if (!productData || !productData.variants || productData.variants.length === 0) {
      console.warn('No product variants found');
      modalBody.innerHTML = '<p style="padding: 20px; text-align: center;">No variants available</p>';
      if (modal) {
        modal.removeAttribute('data-rebuilding');
        modalState.isRebuilding = false;
      }
      return Promise.reject('No variants');
    }

    productData.variants.forEach(variant => {
      let maxQty = 9999; // Default
      
      // If we have inventory_quantity set, use it (from our extraction)
      if (variant.inventory_quantity !== undefined) {
        maxQty = Math.max(0, variant.inventory_quantity);
        console.log(`Using extracted inventory for ${variant.id}: ${maxQty}`);
      }
      // Otherwise check if tracking is enabled
      else if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
        maxQty = Math.max(0, variant.inventory_quantity || 0);
        console.log(`Using API inventory for ${variant.id}: ${maxQty}`);
      }

      const variantItem = document.createElement('div');
      variantItem.className = 'shade-variant-item';
      variantItem.setAttribute('data-variant-id', variant.id);
      variantItem.setAttribute('data-inventory-quantity', maxQty);
      // The Liquid path emits this (data-product-handle="{{ product.handle }}")
      // and the promo layer reads it: applyPromoToShadeModal in theme.liquid
      // falls back to the handle in the PAGE URL when a row has none. Rows built
      // here can belong to a product the page is not about — opened from the
      // added-to-basket panel, or switched on a combined listing — so without it
      // one product's promo gets priced onto another product's shades.
      variantItem.setAttribute('data-product-handle', productData.handle || '');

      const imageHTML = variant.featured_image 
        ? `<img class="shade-variant-image" loading="lazy" decoding="async" width="40" height="40" sizes="${THUMB_SIZES}" srcset="${thumbSrcset(variant.featured_image.src)}" alt="${variant.featured_image.alt || ''}">`
        : '';

      variantItem.innerHTML = `
        ${imageHTML}
        <div class="shade-variant-info">
          <div class="shade-variant-title">${variant.title || variant.name}</div>
          <div class="shade-variant-price" data-original-price="${variant.price}" data-variant-sku="${variant.sku || ''}">${formatMoney(variant.price)}</div>
          <div class="shade-variant-stock">
            <span class="shade-variant-stock-status">&nbsp;</span>
          </div>
        </div>
        <div class="shade-quantity-selector">
          <button type="button" class="shade-qty-btn minus" aria-label="Decrease quantity">-</button>
          <input type="number" class="shade-qty-input" value="0" min="0" aria-label="Quantity">
          <button type="button" class="shade-qty-btn plus" aria-label="Increase quantity">+</button>
        </div>
      `;

      // Sold out, and the partner line carries this exact shade in stock? Offer it.
      const cfg = getSwapConfig();
      const shade = variant.option1 || variant.title;
      const pair = (cfg && maxQty <= 0 && productData.handle === cfg.sourceHandle && cfg.pairs)
        ? cfg.pairs[shade]
        : null;

      if (pair) {
        variantItem.classList.add('shade-variant-item--replaced');

        const deadSelector = variantItem.querySelector('.shade-quantity-selector');
        if (deadSelector) {
          deadSelector.outerHTML = '<span class="shade-variant-unavailable">unavailable</span>';
        }

        const group = document.createElement('div');
        group.className = 'shade-swap-group';
        group.appendChild(variantItem);
        group.insertAdjacentHTML(
          'beforeend',
          swapBlockHTML({ id: variant.id, price: variant.price, sku: variant.sku }, pair, getSwapStyle())
        );
        modalBody.appendChild(group);
      } else {
        modalBody.appendChild(variantItem);
      }
    });

    console.log('✅ Modal rebuilt with', productData.variants.length, 'variants');
    
    // Re-initialize modal after rebuilding
    initializeModal();
    
    // Remove rebuilding flag
    if (modal) {
      modal.removeAttribute('data-rebuilding');
      modalState.isRebuilding = false;
    }
    // Apply promo prices if promo system is active
if (window.promoSystem && window.promoSystem.applyPromoToShadeModal) {
  setTimeout(() => {
    window.promoSystem.applyPromoToShadeModal();
  }, 100);
}
    // CRITICAL: Fetch fresh cart data to update stock availability
    console.log('Fetching cart data to update stock availability...');
    return getCart()
      .then(cart => {
        updateModalInventory(cart);
        console.log('✅ Stock availability updated');
      })
      .catch(error => {
        console.error('Error fetching cart for stock update:', error);
      });
  
  // Apply promo prices if promo system is active
if (window.promoSystem && window.promoSystem.applyPromoToShadeModal) {
  setTimeout(() => {
    window.promoSystem.applyPromoToShadeModal();
  }, 100);
}
    }

  function updateModalInventory(cart) {
    const modal = document.getElementById('shade-modal');
    if (!modal) return;

    console.log('=== UPDATING MODAL INVENTORY ===');
    console.log('Cart items:', cart.items);

    const allVariantItems = modal.querySelectorAll('.shade-variant-item');
    console.log('Total variant items in modal:', allVariantItems.length);
    
    allVariantItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const inventoryQty = parseInt(item.getAttribute('data-inventory-quantity'));
      const cartItem = cart.items.find(i => i.id == variantId);
      const qtyInCart = cartItem ? cartItem.quantity : 0;
      const adjustedMax = inventoryQty - qtyInCart;
      
      console.log(`Variant ${variantId}:`, {
        inventoryQty,
        qtyInCart,
        adjustedMax
      });
      
      item.setAttribute('data-adjusted-max', adjustedMax);

      const stockStatusSpan = item.querySelector('.shade-variant-stock-status');
      // A row replaced by a substitute is presentational and carries no stepper,
      // so every stepper operation below has to tolerate its absence.
      const qtySelector = item.querySelector('.shade-quantity-selector');
      const qtyInput = item.querySelector('.shade-qty-input');
      const inputs = qtySelector ? qtySelector.querySelectorAll('input, button') : [];
      if (stockStatusSpan) stockStatusSpan.className = 'shade-variant-stock-status';

      if (adjustedMax <= 0) {
        if (stockStatusSpan) {
          if (qtyInCart > 0) {
            stockStatusSpan.textContent = 'All in your basket';
            stockStatusSpan.classList.add('stock-low');
          } else {
            stockStatusSpan.textContent = 'Out of Stock';
            stockStatusSpan.classList.add('stock-oos');
          }
        }
        if (qtySelector) qtySelector.classList.add('disabled');
        inputs.forEach(el => el.disabled = true);
        if (qtyInput) qtyInput.value = 0;
      } else if (adjustedMax < 10) {
        if (stockStatusSpan) {
          stockStatusSpan.textContent = `${adjustedMax} left`;
          stockStatusSpan.classList.add('stock-low');
        }
        if (qtySelector) qtySelector.classList.remove('disabled');
        inputs.forEach(el => el.disabled = false);
      } else {
        if (stockStatusSpan) {
          stockStatusSpan.textContent = 'In Stock';
          stockStatusSpan.classList.add('stock-in');
        }
        if (qtySelector) qtySelector.classList.remove('disabled');
        inputs.forEach(el => el.disabled = false);
      }
    });
    
    console.log('=== INVENTORY UPDATE COMPLETE ===');
  }

  // --- mirrored verbatim from tests/bulk-shade-modal.mjs -----------------------
  // Change there, run `node --test`, then mirror here.
  function shadeModalTarget(state) {
    var s = state || {};
    var wanted = s.wanted || s.pageProduct || null;
    if (!wanted) return null;
    if (s.needsRebuild) return wanted;
    return s.builtFor === wanted ? null : wanted;
  }
  // --- end mirrored helper -----------------------------------------------------

  // Which product THIS page is about. Combined listings swap it under the page,
  // so it is read fresh every time; null off a product page.
  function currentPageProductUrl() {
    const productEl = document.getElementById('product');
    if (!productEl) return null;

    let productUrl = productEl.dataset.productUrl;

    if (!productUrl) {
      const productForm = document.querySelector('.product-form, [data-type="add-to-cart-form"]');
      if (productForm && productForm.action) {
        productUrl = productForm.action.split('?')[0].replace('/cart/add', '');
      }
    }

    if (!productUrl) {
      productUrl = window.location.pathname;
    }

    return productUrl ? productUrl.split('?')[0] : null;
  }

  // Opened by the page's own #open-shade-modal button, so the product wanted is
  // always this page's. It cannot just open what is there: the added-to-basket
  // panel may have rebuilt the list for a product this page is not about, and a
  // bulk add from that list would add the wrong product's variants.
  function openModal() {
    const { modal, overlay } = getModalElements();
    if (!modal || !overlay) return;

    const builtFor = modalState.currentProductUrl;
    const target = shadeModalTarget({
      builtFor: builtFor,
      pageProduct: currentPageProductUrl(),
      needsRebuild: modalState.needsRebuild
    });

    if (!target) { showModal(); return; }

    console.log('Modal needs rebuild before opening');
    rebuildModalForUrl(target)
      .then(() => {
        modalState.needsRebuild = false;
        showModal();
      })
      .catch(error => {
        console.error('Failed to rebuild modal:', error);
        // "Open it anyway" is only safe while the list still belongs to this
        // page's product. If the panel had rebuilt it for another one, opening
        // would put the shopper on that product's shades with nothing saying so
        // — reload instead, which re-renders the modal server-side for this page.
        if (builtFor && builtFor !== target) { window.location.reload(); return; }
        showModal();
      });
  }

  // The open itself — everything past the rebuild decision. Callers that have
  // already put the right product in the list use this directly.
  function showModal() {
    const { modal, overlay } = getModalElements();
    if (!modal || !overlay) return;

    modalState.isOpen = true;
    modal.classList.add('loading');
    overlay.classList.add('active');
    modal.classList.add('active');
    if (modalState.scrollTopOnShow) {
      const scroller = modal.querySelector('.shade-modal-body');
      if (scroller) scroller.scrollTop = 0;
      modalState.scrollTopOnShow = false;
    }
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');

    getCart(true) // Force refresh on open
      .then(cart => {
        updateModalInventory(cart);
        modal.classList.remove('loading');
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
        modal.classList.remove('loading');
      });
  }

  function _unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  }

  function closeModal(force = false) {
    const { modal, overlay } = getModalElements();
    if (!modal || !overlay) return;

    // Check if user has selections and hasn't forced close
    if (!force && hasSelections()) {
      showConfirmationModal().then(userWantsToAdd => {
        if (userWantsToAdd) {
          handleBulkAddToCart();
        } else {
          closeModal(true);
        }
      }).catch(function () {
        // Confirmation modal failed — force close so overflow never gets stuck
        closeModal(true);
      });
      // Do NOT return before unlocking scroll — unlock now so dropdown menus
      // aren't clipped if the confirmation flow stalls or errors.
      _unlockScroll();
      return;
    }

    // Clear all selections when closing
    const allVariantItems = modal.querySelectorAll('.shade-variant-item');
    allVariantItems.forEach(item => {
      const input = item.querySelector('.shade-qty-input');
      if (input) input.value = 0;
    });

    modalState.isOpen = false;
    modal.classList.remove('active');
    overlay.classList.remove('active');
    _unlockScroll();
  }

  function handleQuantitySelector(selector) {
    const minusBtn = selector.querySelector('.minus');
    const plusBtn = selector.querySelector('.plus');
    const input = selector.querySelector('.shade-qty-input');
    const item = selector.closest('.shade-variant-item');

    if (!minusBtn || !plusBtn || !input || !item) return;

    // Remove old listeners by cloning
    const newMinusBtn = minusBtn.cloneNode(true);
    const newPlusBtn = plusBtn.cloneNode(true);
    const newInput = input.cloneNode(true);
    
    minusBtn.parentNode.replaceChild(newMinusBtn, minusBtn);
    plusBtn.parentNode.replaceChild(newPlusBtn, plusBtn);
    input.parentNode.replaceChild(newInput, input);

    newMinusBtn.addEventListener('click', function() {
      if (newMinusBtn.disabled) return;
      let currentValue = parseInt(newInput.value);
      if (currentValue > 0) {
        newInput.value = currentValue - 1;
      }
    });

    newPlusBtn.addEventListener('click', function() {
      if (newPlusBtn.disabled) return;
      const maxQuantity = parseInt(item.getAttribute('data-adjusted-max'));
      let currentValue = parseInt(newInput.value);
      
      if (currentValue < maxQuantity) {
        newInput.value = currentValue + 1;
      } else {
        newInput.value = maxQuantity;
      }
    });

    newInput.addEventListener('input', function() {
      if (newInput.disabled) return;
      const maxQuantity = parseInt(item.getAttribute('data-adjusted-max'));
      let currentValue = parseInt(newInput.value);
      
      if (isNaN(currentValue) || currentValue < 0) {
        newInput.value = 0;
      } else if (currentValue > maxQuantity) {
        newInput.value = maxQuantity;
      }
    });
  }

  function handleBulkAddToCart() {
    const bulkAddBtn = document.getElementById('bulk-add-to-cart');
    const modal = document.getElementById('shade-modal');
    
    if (!bulkAddBtn || !modal) return;

    // CRITICAL: Check if modal is being rebuilt
    if (modal.hasAttribute('data-rebuilding') || modalState.isRebuilding) {
      alert('Please wait while the product updates...');
      return;
    }

    const allVariantItems = modal.querySelectorAll('.shade-variant-item');
    const itemsToAdd = [];

    allVariantItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const input = item.querySelector('.shade-qty-input');
      if (!input) return; // row replaced by a substitute — nothing of its own to add
      const quantity = parseInt(input.value);

      if (quantity > 0) {
        var line = {
          id: variantId,
          quantity: quantity
        };

        // A substitute carries the sold-out shade it stands in for. The worker
        // reads these off the cart line and holds that shade's tier price on
        // this one; without them the line is priced as an ordinary 3Deluxe.
        // Both are `_`-prefixed so the cart templates keep them out of sight.
        var swapFor = item.getAttribute('data-swap-for');
        if (swapFor) {
          line.properties = {
            _substitute_for: swapFor,
            _substitute_for_sku: item.getAttribute('data-swap-for-sku') || ''
          };
        }

        itemsToAdd.push(line);
      }
    });

    if (itemsToAdd.length === 0) {
      alert('Please select a quantity for at least one shade.');
      return;
    }

    bulkAddBtn.textContent = 'Adding...';
    bulkAddBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: itemsToAdd })
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      return response.json().then(errorData => {
        throw new Error(errorData.description || errorData.message || 'Error adding items.');
      });
    })
    .then(cart => {
      closeModal(true); // Force close without prompt
      bulkAddBtn.textContent = 'Add to Cart';
      bulkAddBtn.disabled = false;
      
      // Clear cart cache
      cartFetchPromise = null;
      
      allVariantItems.forEach(item => {
        const input = item.querySelector('.shade-qty-input');
        if (input) input.value = 0;
      });

      const cartSectionId = 'cart-drawer';
      const cartBubbleSelector = '#CartCount';
      const cartDrawerSelector = '#CartDrawer';
      const cartDrawerContentSelector = '.drawer__inner-part';
      const cartTriggerSelector = 'a[href="/cart"], button[aria-controls="CartDrawer"], .headerCart';
      const bubbleSectionId = 'cart-icon-bubble';

      fetch(`/?sections=${cartSectionId},${bubbleSectionId}`)
        .then(response => response.json())
        .then(sections => {
          const drawerContainer = document.querySelector(cartDrawerSelector);
          const newDrawerHTML = sections[cartSectionId];
          
          if (drawerContainer && newDrawerHTML) {
            const innerContent = drawerContainer.querySelector(cartDrawerContentSelector);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = newDrawerHTML;
            const newInnerContent = tempDiv.querySelector(cartDrawerContentSelector);
            
            if (innerContent && newInnerContent) {
              innerContent.innerHTML = newInnerContent.innerHTML;
            } else if (drawerContainer) {
              const newMainContent = tempDiv.querySelector(cartDrawerSelector);
              if (newMainContent) {
                drawerContainer.innerHTML = newMainContent.innerHTML;
              } else {
                drawerContainer.innerHTML = newDrawerHTML;
              }
            }
          }

          const bubbleContainer = document.querySelector(cartBubbleSelector);
          let newBubbleHTML = sections[bubbleSectionId];
          
          if (!newBubbleHTML && newDrawerHTML) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = newDrawerHTML;
            const bubbleInDrawer = tempDiv.querySelector(cartBubbleSelector);
            if (bubbleInDrawer) {
              newBubbleHTML = bubbleInDrawer.innerHTML;
            }
          }

          if (bubbleContainer && newBubbleHTML) {
            bubbleContainer.innerHTML = newBubbleHTML;
          }

          const cartTrigger = document.querySelector(cartTriggerSelector);
          if (cartTrigger) {
            setTimeout(() => cartTrigger.click(), 50);
          }
        })
        .catch(error => {
          console.error('Error fetching section HTML for cart refresh:', error);
          alert('Items added! Please open your cart manually to see updates.');
        });
    })
    .catch(error => {
      console.error('Error:', error.message);
      if (error.message.includes('Inventory') || error.message.includes('out of stock')) {
        alert('Could not add all items: ' + error.message + ' Please adjust quantities.');
      } else {
        alert('There was an error: ' + error.message);
      }
      bulkAddBtn.textContent = 'Add to Cart';
      bulkAddBtn.disabled = false;
      
      // Clear cache and refresh inventory
      cartFetchPromise = null;
      getCart(true).then(cart => updateModalInventory(cart));
    });
  }

  function initializeModal() {
    const { modal, overlay, closeBtn, bulkAddBtn } = getModalElements();
    
    if (!modal || !overlay) return;

    if (closeBtn) {
      const newCloseBtn = closeBtn.cloneNode(true);
      closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
      newCloseBtn.addEventListener('click', () => closeModal(false));
    }

    if (overlay) {
      const newOverlay = overlay.cloneNode(true);
      overlay.parentNode.replaceChild(newOverlay, overlay);
      newOverlay.addEventListener('click', () => closeModal(false));
    }

    const quantitySelectors = modal.querySelectorAll('.shade-quantity-selector');
    quantitySelectors.forEach(handleQuantitySelector);

    if (bulkAddBtn) {
      const newBulkAddBtn = bulkAddBtn.cloneNode(true);
      bulkAddBtn.parentNode.replaceChild(newBulkAddBtn, bulkAddBtn);
      newBulkAddBtn.addEventListener('click', handleBulkAddToCart);
    }

    console.log('✅ Modal initialized with', quantitySelectors.length, 'quantity selectors');
  }

  document.body.addEventListener('click', function(event) {
    if (event.target.matches('#open-shade-modal') || event.target.closest('#open-shade-modal')) {
      event.preventDefault();
      openModal();
    }
  });

  // ============================================================================
  // FETCH PRODUCT DATA AND REBUILD MODAL
  // ============================================================================
  
  // Rebuild the modal for the product currently on screen.
  function fetchCurrentProductAndRebuildModal() {
    const productUrl = currentPageProductUrl();
    if (!productUrl) {
      console.warn('Product element not found');
      return Promise.reject('No product element');
    }
    return rebuildModalForUrl(productUrl);
  }

  // Rebuild the modal for ANY product, named by URL — not necessarily the one the
  // page was rendered for. That is what lets the added-to-basket panel open the
  // shade list for a product quick-shopped from a collection grid.
  function rebuildModalForUrl(productUrl) {
    if (!productUrl) {
      console.warn('Product URL not found');
      return Promise.reject('No product URL');
    }

    // CRITICAL: Remove any query parameters (like ?variant=) before adding .js
    productUrl = productUrl.split('?')[0];

    // Prevent duplicate fetches for same product
    if (modalState.currentProductUrl === productUrl && !modalState.needsRebuild && !modalState.isRebuilding) {
      console.log('Modal already built for this product');
      return Promise.resolve();
    }
    
    modalState.currentProductUrl = productUrl;

    console.log('Fetching product data from:', productUrl + '.js');

    // Fetch both the .js endpoint AND the HTML page
    return Promise.all([
      fetch(productUrl + '.js').then(res => res.json()),
      fetch(productUrl).then(res => res.text())
    ])
      .then(([productData, htmlText]) => {
        console.log('Product data received:', productData);
        
        // Parse HTML to get inventory data
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        
        // Extract inventory quantities from the HTML
        const inventoryMap = {};
        
        // Method 1: Look for #pvr-{variantId} elements
        productData.variants.forEach(variant => {
          const pvrElement = doc.getElementById(`pvr-${variant.id}`);
          if (pvrElement) {
            const qty = parseInt(pvrElement.textContent.trim());
            if (!isNaN(qty)) {
              inventoryMap[variant.id] = qty;
              console.log(`Found inventory for ${variant.id}: ${qty}`);
            }
          }
        });
        
        // Method 2: Look for data attributes (fallback)
        if (Object.keys(inventoryMap).length === 0) {
          const inventoryElements = doc.querySelectorAll('[data-variant-id][data-inventory-quantity]');
          inventoryElements.forEach(el => {
            const variantId = el.dataset.variantId;
            const qty = parseInt(el.dataset.inventoryQuantity);
            if (variantId && !isNaN(qty)) {
              inventoryMap[variantId] = qty;
              console.log(`Found inventory via data-attr for ${variantId}: ${qty}`);
            }
          });
        }
        
        console.log('Inventory map extracted:', inventoryMap);
        
        // Merge inventory data into product variants
        productData.variants.forEach(variant => {
          if (inventoryMap[variant.id] !== undefined) {
            variant.inventory_quantity = inventoryMap[variant.id];
            console.log(`✅ Variant ${variant.id} inventory set to:`, variant.inventory_quantity);
          } else {
            console.warn(`⚠️ No inventory found for variant ${variant.id}, keeping default`);
          }
        });

        // Take the substitute pairs from the SAME document. #shade-swap-data is
        // rendered only on the source product's own page, so a modal rebuilt from
        // a collection page would otherwise silently drop every swap and show a
        // bare sold-out row where an in-stock twin exists.
        adoptSwapData(doc);

        return rebuildModalForProduct(productData);
      })
      .catch(error => {
        console.error('Error fetching product data:', error);
        modalState.needsRebuild = true;
        // The modal was never built for this URL, so it must not be remembered as
        // built — otherwise the dedupe above would skip the retry.
        modalState.currentProductUrl = null;
        throw error;
      });
  }

  // Replace the in-page swap pairs with the ones belonging to the product just
  // fetched — including removing them when that product has none, so a previous
  // product's substitutes can never be offered against this one's shades.
  function adoptSwapData(doc) {
    const fresh = doc.getElementById('shade-swap-data');
    const current = document.getElementById('shade-swap-data');

    if (!fresh) {
      if (current) current.remove();
      _swapConfig = undefined; // undefined = "not parsed yet", not "no swaps"
      return;
    }

    if (current && current.textContent === fresh.textContent) return;

    if (current) {
      current.textContent = fresh.textContent;
    } else {
      const el = document.createElement('script');
      el.type = 'application/json';
      el.id = 'shade-swap-data';
      el.textContent = fresh.textContent;
      document.body.appendChild(el);
    }
    _swapConfig = undefined;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  // Open the modal for a named product, which may not be the one this page was
  // rendered for — the added-to-basket panel's "Add several shades at once" button can
  // fire from a collection grid (quick-shop) as readily as from a PDP.
  //
  // Resolves only once the modal is open on THAT product's shades. It rejects
  // rather than opening something wrong: an unrebuilt modal would show another
  // product's shades, or none at all off a product page, so the caller falls back
  // to the product page instead.
  window.BulkShadeModal = {
    openForProduct: function (productUrl) {
      if (!productUrl) return Promise.reject(new Error('No product URL'));
      const { modal, overlay } = getModalElements();
      if (!modal || !overlay) return Promise.reject(new Error('Shade modal is not on this page'));

      const wanted = String(productUrl).split('?')[0];
      const target = shadeModalTarget({
        builtFor: modalState.currentProductUrl,
        pageProduct: currentPageProductUrl(),
        wanted: wanted,
        needsRebuild: modalState.needsRebuild
      });

      // Already the right product — the server-rendered list is better than
      // anything a rebuild would produce (real Liquid inventory, real swap rows).
      // showModal(), not openModal(): openModal exists to force the list back to
      // the PAGE's product, which here is exactly what must not happen.
      if (!target) {
        showModal();
        return Promise.resolve();
      }

      return rebuildModalForUrl(target).then(function () {
        modalState.needsRebuild = false;
        showModal();
      });
    }
  };

  // ============================================================================
  // INITIALIZATION & OBSERVERS
  // ============================================================================

  // CRITICAL: Single initialization point
  function performInitialization() {
    if (window._bulkShadeModalFunctionsInitialized) {
      console.log('Modal functions already initialized');
      return;
    }
    window._bulkShadeModalFunctionsInitialized = true;
    
    initAutoSelect();
    initializeModal();

    // Liquid renders whichever presentation the theme setting asks for; a ?swap=
    // override only has to re-skin what's already there.
    var modal = document.getElementById('shade-modal');

    // The list Liquid rendered already belongs to a product — say which, so that
    // asking to open it for that same product doesn't refetch and rebuild it.
    // A rebuild is strictly worse here: it re-derives inventory by scraping the
    // page it just fetched. Absent on non-product templates, where the list is
    // empty and every open must rebuild.
    var rendered = modal && modal.getAttribute('data-product-url');
    if (rendered && !modalState.currentProductUrl) {
      modalState.currentProductUrl = rendered.split('?')[0];
    }

    var wanted = getSwapStyle();
    if (modal && modal.getAttribute('data-swap-style') !== wanted) {
      applySwapStyle(wanted);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', performInitialization);
  } else {
    performInitialization();
  }

  // Listen for product updates from combined listing
  document.addEventListener('product:updated', function() {
    if (pendingAutoSelect) {
      clearTimeout(pendingAutoSelect);
      pendingAutoSelect = null;
    }
    
    // CRITICAL: Check if modal is open
    const isModalOpen = modalState.isOpen;
    
    setTimeout(function() {
      initAutoSelect();
      
      // Only rebuild if modal is NOT open
      if (!isModalOpen) {
        fetchCurrentProductAndRebuildModal();
      } else {
        // If modal is open, mark it as stale and rebuild on next open
        console.log('Modal is open, marking for rebuild on next open');
        modalState.needsRebuild = true;
      }
    }, 100);
  });

  const autoSelectObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList && (node.classList.contains('swatch') || 
                node.querySelector && node.querySelector('.swatch'))) {
              const container = node.closest('product-info, .product-single__meta, #product');
              if (container) {
                const existingSwatch = container.querySelector('.swatchInput:checked');
                if (!existingSwatch) {
                  setTimeout(() => autoSelectFirstSwatch(container, true), 50);
                }
              }
            }
          }
        });
      }
    });
  });

  const modalObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        const modal = document.getElementById('shade-modal');
        if (modal && !modal.hasAttribute('data-initialized')) {
          console.log('Modal added to DOM, initializing...');
          modal.setAttribute('data-initialized', 'true');
          initializeModal();
        }
      }
    });
  });

  const productSection = document.querySelector('#product, [data-section-type="product"], .ptContainer');
  if (productSection) {
    autoSelectObserver.observe(productSection, {
      childList: true,
      subtree: true
    });
    
    modalObserver.observe(productSection, {
      childList: true,
      subtree: true
    });
  }

})();
